var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import BigNumber from 'bignumber.js';
import { getIcon } from './getIcon';
import { getSuiClient } from './client';
import { getTxErrorMessage } from './txResult';
import { validateNetwork } from './networkUtils';
const EXPLORER_BASE_URL = 'https://suiscan.xyz';
const MIN_SUI_BALANCE = 0.1;
const SUI_DECIMALS = 9;
/**
 * Gas budget for the receipt transfer, in MIST. 0.05 SUI, below the 0.1 SUI MIN_SUI_BALANCE
 * floor enforced before signing. Pinned because the wallet's auto-estimate has come in under
 * the real cost on the money path and reverted transactions on-chain with InsufficientGas.
 * See transactionHandlers.ts for the full note.
 */
const GAS_BUDGET_MIST = 50000000;
const createExplorerLink = (network, digest) => {
    const networkSuffix = network === 'testnet' ? 'testnet' : 'mainnet';
    return `${EXPLORER_BASE_URL}/${networkSuffix}/tx/${digest}`;
};
function shortenAddress(addr) {
    if (addr.length <= 12)
        return addr;
    return `${addr.slice(0, 6)}….${addr.slice(-4)}`;
}
function getSuccessNotification(actionLabel, explorerLink, receiverAddress) {
    switch (actionLabel) {
        case 'Initiate Transfer':
            return {
                title: 'Position Transfer Initiated',
                description: 'Please confirm the transfer after the 48-hour cooldown period ends.',
                link: explorerLink,
                image: getIcon('success'),
                spinner: false,
            };
        case 'Abort Transfer':
            return {
                title: 'Position Transfer Aborted',
                description: 'Position transfer has been successfully aborted.',
                link: explorerLink,
                image: getIcon('success'),
                spinner: false,
            };
        case 'Confirm Transfer':
            return {
                title: 'Position Transferred Successfully',
                description: (receiverAddress &&
                    `Your position was successfully transferred to ${shortenAddress(receiverAddress)}.`) ||
                    'Your position was successfully transferred.',
                link: explorerLink,
                image: getIcon('success'),
                spinner: false,
            };
        default:
            return {
                title: `${actionLabel} Successful`,
                description: `${actionLabel} transaction completed successfully.`,
                link: explorerLink,
                image: getIcon('success'),
                spinner: false,
            };
    }
}
function getUserCancelNotification(actionLabel) {
    switch (actionLabel) {
        case 'Abort Transfer':
            return {
                title: 'Transaction Cancelled',
                description: 'The transaction was cancelled by the user.',
                image: getIcon('failed'),
                spinner: false,
            };
        case 'Initiate Transfer':
        case 'Confirm Transfer':
        default:
            return {
                title: 'Position Transfer Cancelled',
                description: 'The transaction was cancelled by the user.',
                image: getIcon('failed'),
                spinner: false,
            };
    }
}
function getOnChainFailureNotification(actionLabel, errMsg, explorerLink) {
    return {
        title: `${actionLabel} Failed`,
        description: errMsg,
        link: explorerLink,
        image: getIcon('failed'),
        spinner: false,
    };
}
export const transferReceiptTransactionHandler = ({ transaction, actionLabel, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, onSuccess, clearUserCaches, address, suiBalance, network, onSettled, receiverAddress, }) => {
    const suiBalanceDecimal = new BigNumber(suiBalance).dividedBy(new BigNumber(10).pow(SUI_DECIMALS));
    if (suiBalanceDecimal.lt(MIN_SUI_BALANCE)) {
        setNotificationInfo({
            title: 'Insufficient Gas Balance',
            description: `At least ${MIN_SUI_BALANCE} SUI is required in your wallet to cover gas fees.`,
            image: getIcon('failed'),
            spinner: false,
        });
        setIsNotificationOpen(true);
        onSettled === null || onSettled === void 0 ? void 0 : onSettled();
        return;
    }
    const suiClient = getSuiClient(validateNetwork(network));
    setNotificationInfo({
        title: 'Awaiting Response',
        description: 'Requesting wallet to approve transaction.',
        spinner: true,
        image: null,
    });
    setIsNotificationOpen(true);
    transaction.setGasBudget(GAS_BUDGET_MIST);
    signAndExecuteTransaction({ transaction }, {
        onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const explorerLink = createExplorerLink(network, result.digest);
                if (result.success) {
                    setNotificationInfo(getSuccessNotification(actionLabel, explorerLink, receiverAddress));
                }
                else {
                    const txResult = yield suiClient
                        .waitForTransaction({ digest: result.digest, include: { effects: true } })
                        .catch(() => null);
                    const errMsg = (txResult && getTxErrorMessage(txResult)) || 'Transaction reverted on-chain.';
                    setNotificationInfo(getOnChainFailureNotification(actionLabel, errMsg, explorerLink));
                }
                setIsNotificationOpen(true);
                clearUserCaches(address);
                setTimeout(() => onSuccess(), 1000);
            }
            catch (err) {
                console.error('waitForTransaction error:', err);
                setNotificationInfo({
                    title: `${actionLabel} Failed`,
                    description: 'Could not confirm transaction status. Please check the explorer.',
                    link: createExplorerLink(network, result.digest),
                    image: getIcon('failed'),
                    spinner: false,
                });
                setIsNotificationOpen(true);
                clearUserCaches(address);
                setTimeout(() => onSuccess(), 1000);
            }
            finally {
                onSettled === null || onSettled === void 0 ? void 0 : onSettled();
            }
        }),
        onError: (err) => {
            onSettled === null || onSettled === void 0 ? void 0 : onSettled();
            console.error(`${actionLabel} transaction error:`, err);
            setNotificationInfo(getUserCancelNotification(actionLabel));
            setIsNotificationOpen(true);
            clearUserCaches(address);
            onSuccess();
        },
    });
};
